import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * PiperRailContext — pages publish their rail content (briefing, action
 * cards, suggested questions) and the AppLayout renders the 340px Piper rail
 * only when a page has published something.
 */
const PiperRailContext = createContext({ config: null, setConfig: () => {} });

export const PiperRailProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const value = useMemo(() => ({ config, setConfig }), [config]);
  return <PiperRailContext.Provider value={value}>{children}</PiperRailContext.Provider>;
};

export const usePiperRailContext = () => useContext(PiperRailContext);

/**
 * Pages call this to mount content into the Piper rail for their lifetime.
 * `config` shape:
 * {
 *   surface: 'pipeline' | 'pricer' | 'loan',
 *   status: string,                  // "watching all 24 loans"
 *   briefing: string,
 *   cards: PiperActionCard[],        // optional
 *   questions: string[],             // suggested-question chips
 *   onAction(card, action),          // optional
 *   loanId,                          // optional — enables loan-scoped AI chat
 *   scenarioChat: boolean,           // optional — enables Quick Pricer scenario chat
 *   getScenarioSnapshot: () => ({ pricingRequest, lastRunSummary }),
 *   extra: ReactNode,                // optional custom rail content
 * }
 */
export const usePiperRail = (config) => {
  const { setConfig } = usePiperRailContext();
  // Fingerprint the serializable parts so pages can pass a fresh object every
  // render without causing a publish loop (functions/nodes are excluded).
  const fingerprint = JSON.stringify(config, (key, value) =>
    typeof value === 'function' || key === 'extra' ? undefined : value);
  useEffect(() => {
    setConfig(config);
    return () => setConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, setConfig]);
};

export default PiperRailContext;
